import React, { useState, useEffect, useMemo } from "react";
import { Typography, Grid, Box, FormControlLabel, Switch } from '@mui/material';
import { Container } from "@mui/system";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { format } from "date-fns";
import { Link } from 'react-router-dom';

import { publicApi } from "../../api/http";
import ReviewDiscussionSection from "../../components/ReviewDiscussion/ReviewDiscussionSection";
import GridListViewToggle from "../../components/GridListViewToggle/GridListViewToggle";
import MediaRankingPlayers from "../../components/MediaRankingPlayers/MediaRankingPlayers";
import { brandSwitchSx } from "../../theme/brandSwitchSx";
import "../MocksAndRankings/MediaRankingLayout.css";


function Ranking() {
    const [ranking, setRanking] = useState(null);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    const [showConsensusComparison, setShowConsensusComparison] = useState(true);

    const { ranking_id } = useParams();
    useEffect(() => {
        publicApi.get("/rankings/" + ranking_id).then((response) => {
            setRanking(response.data);
        })
            .catch((error) => {
                setError(error);
            });
    }, [ranking_id]);

    const playerItems = useMemo(() => {
        if (!ranking?.rankings) return [];
        return ranking.rankings.map((ranked_player) => ({
            player: ranked_player.player,
            rank: ranked_player.rank,
        }));
    }, [ranking]);


    return (
        <>
            {ranking && (
                <Helmet>
                    <title>{ranking.title} - Hockey Pipeline</title>
                    <meta name="description" content={`NHL Prospect Ranking by ${ranking.scout.name} from ${ranking.publication.name} for the ${ranking.draft_year} Draft`} />
                    <link rel="canonical" href={`https://hockeypipeline.com/ranking/${ranking_id}`} />
                </Helmet>
            )}
            <Container className="container top-container">
                {ranking && <Grid container spacing={4}>
                    <Grid item xs={12} sm={12} md={12}>
                        <Box className="media-ranking-detail-header">
                            <Typography variant="h4" component="h1">
                                {ranking.title}
                            </Typography>
                            <Box className="media-ranking-detail-toolbar">
                                <Box
                                    className={[
                                        "media-ranking-consensus-panel",
                                        showConsensusComparison
                                            ? "media-ranking-consensus-panel--on"
                                            : "",
                                    ].join(" ")}
                                >
                                    <FormControlLabel
                                        className="media-ranking-consensus-toggle"
                                        labelPlacement="start"
                                        control={
                                            <Switch
                                                size="small"
                                                checked={showConsensusComparison}
                                                onChange={(e) =>
                                                    setShowConsensusComparison(e.target.checked)
                                                }
                                                sx={brandSwitchSx}
                                                inputProps={{
                                                    "aria-label": "Show consensus comparison overlay",
                                                }}
                                            />
                                        }
                                        label="Consensus overlay"
                                    />
                                </Box>
                                <GridListViewToggle
                                    value={viewMode}
                                    onChange={setViewMode}
                                    ariaLabel="Ranking layout"
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography className="sub-header-text">
                            {`Creation Date: ${format(new Date(ranking.creation_date), "MMM do, yyyy")}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography className="sub-header-text" sx={{ mb: 1 }}>
                            Author: <Link to={"../media-rankings?scouts_id=" + ranking.scouts_id} className="link-default">{ranking.scout.name}</Link>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography className="sub-header-text" sx={{ mb: 1 }}>
                            Publication: <Link to={"../media-rankings?publication_id=" + ranking.publication.publication_id} className="link-default">{ranking.publication.name}</Link>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography className="sub-header-text">
                            Draft Year: {ranking.draft_year}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12}>
                        <Typography className="sub-header-text">
                            If you want to see the full ranking, click <Link to={ranking.url} className="link-default">here</Link>.
                        </Typography>
                    </Grid>
                    <MediaRankingPlayers
                        items={playerItems}
                        viewMode={viewMode}
                        rankLabel="Rank"
                        showConsensusComparison={showConsensusComparison}
                    />
                    <Grid item xs={12}>
                        <ReviewDiscussionSection
                            reviewType="media"
                            reviewId={ranking.ranking_id}
                            title="Ranking discussion"
                        />
                    </Grid>
                </Grid>}
            </Container>
        </>
    );
};

export default Ranking;
